import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Download, Trash2, Mail, Calendar, Globe, Search, Save, Copy,
  Eye, Settings as SettingsIcon, Layers, Send, Link as LinkIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap, downloadFile } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/Dialog';
import { FormBuilder, type FormFieldData } from '@/features/forms-builder/FormBuilder';
import { FormPreview } from '@/features/forms-builder/FormPreview';

interface FormDetail {
  id: number | string;
  key: string;
  title: string;
  description: string | null;
  submit_label?: string | null;
  fields: FormFieldData[] | null;
  notifications?: {
    recipients?: string[];
    subject?: string;
    reply_to_field?: string;
    webhook_url?: string;
  } | null;
  integrations?: {
    crm?: { provider?: string; endpoint?: string; token?: string };
    mailchimp?: { list_id?: string; api_key?: string };
    zapier?: { hook_url?: string };
    acelle?: { enabled?: boolean; list_uid?: string; tag?: string; double_optin?: boolean };
  } | null;
  redirect_url?: string | null;
  success_message?: string;
  honeypot_enabled?: boolean;
  recaptcha_enabled?: boolean;
  store_submissions?: boolean;
  is_active?: boolean;
}

interface Submission {
  id: number;
  payload: Record<string, unknown>;
  ip: string | null;
  referer: string | null;
  utm_source?: string | null;
  utm_campaign?: string | null;
  created_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

export const Route = createFileRoute('/_app/forms/$id')({
  component: FormDetailPage,
});

function FormDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('builder');
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [selectedSub, setSelectedSub] = useState<Submission | null>(null);
  const [draft, setDraft] = useState<FormDetail | null>(null);

  const { data: form } = useQuery({
    queryKey: ['form', id],
    queryFn: () => unwrap<FormDetail>(api.get(`/forms/${id}`)),
  });

  // Initialize draft once
  useEffect(() => {
    if (form && !draft) {
      setDraft({
        ...form,
        fields: ensureIds(form.fields ?? []),
      });
    }
  }, [form, draft]);

  const { data: subs, isLoading: subsLoading } = useQuery({
    queryKey: ['form-submissions', id, page],
    queryFn: () =>
      unwrap<Paginated<Submission>>(api.get(`/forms/${id}/submissions?page=${page}&per_page=25`)),
    enabled: activeTab === 'submissions',
  });

  const save = useMutation({
    mutationFn: () => {
      if (!draft) return Promise.reject();
      const payload = serialize(draft);
      return api.put(`/forms/${id}`, payload);
    },
    onSuccess: () => {
      toast.success('Formulario guardado');
      void qc.invalidateQueries({ queryKey: ['form', id] });
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: () => toast.error('Error al guardar el formulario'),
  });

  const duplicate = useMutation({
    mutationFn: () => api.post<{ data: FormDetail }>(`/forms/${id}/duplicate`),
    onSuccess: (res) => {
      const newId = res.data.data.id;
      toast.success('Formulario duplicado');
      void qc.invalidateQueries({ queryKey: ['forms'] });
      void navigate({ to: '/forms/$id', params: { id: String(newId) } });
    },
  });

  const removeSub = useMutation({
    mutationFn: (sid: number) => api.delete(`/forms/${id}/submissions/${sid}`),
    onSuccess: () => {
      toast.success('Envío eliminado');
      void qc.invalidateQueries({ queryKey: ['form-submissions', id] });
      setSelectedSub(null);
    },
  });

  const removeForm = useMutation({
    mutationFn: () => api.delete(`/forms/${id}`),
    onSuccess: () => {
      toast.success('Formulario eliminado');
      void qc.invalidateQueries({ queryKey: ['forms'] });
      void navigate({ to: '/forms' });
    },
  });

  const filteredSubs = useMemo(() => {
    if (!subs) return [];
    return !q
      ? subs.data
      : subs.data.filter((s) => JSON.stringify(s.payload).toLowerCase().includes(q.toLowerCase()));
  }, [subs, q]);

  async function downloadCsv() {
    try {
      await downloadFile(`/forms/${id}/submissions/export`);
    } catch {
      toast.error('No se pudo exportar el CSV');
    }
  }

  if (!draft) return <div className="p-6 text-sm text-neutral-500">Cargando…</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <Link
        to="/forms"
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
      >
        <ArrowLeft className="size-3" /> Formularios
      </Link>

      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{draft.title || 'Sin título'}</h1>
          <p className="text-sm text-neutral-500">
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs">/{draft.key}</code> ·
            {' '}{(draft.fields ?? []).length} campos
            {subs && ` · ${subs.total} envíos`}
            {draft.is_active === false && <Badge variant="outline" className="ml-2">Inactivo</Badge>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => duplicate.mutate()}>
            <Copy className="size-4" /> Duplicar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (confirm('¿Eliminar este formulario? Se perderán los envíos asociados.')) {
                removeForm.mutate();
              }
            }}
          >
            <Trash2 className="size-4 text-red-600" />
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            <Save className="size-4" /> {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="builder">
            <Layers className="mr-1 size-3.5" /> Builder
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="mr-1 size-3.5" /> Preview
          </TabsTrigger>
          <TabsTrigger value="settings">
            <SettingsIcon className="mr-1 size-3.5" /> Ajustes
          </TabsTrigger>
          <TabsTrigger value="submissions">
            <Send className="mr-1 size-3.5" /> Envíos
          </TabsTrigger>
        </TabsList>

        {/* BUILDER */}
        <TabsContent value="builder">
          <FormBuilder
            fields={draft.fields ?? []}
            onChange={(fields) => setDraft({ ...draft, fields })}
          />
        </TabsContent>

        {/* PREVIEW */}
        <TabsContent value="preview">
          <FormPreview
            title={draft.title}
            description={draft.description ?? undefined}
            fields={draft.fields ?? []}
            submitLabel={draft.submit_label ?? 'Enviar'}
          />
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings">
          <div className="grid gap-4 md:grid-cols-2">
            <SectionCard title="General" description="Datos públicos del formulario">
              <div className="space-y-1">
                <Label>Título</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Clave técnica (key)</Label>
                <Input
                  value={draft.key}
                  onChange={(e) =>
                    setDraft({ ...draft, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-') })
                  }
                />
                <p className="text-[10px] text-neutral-400">
                  Cambiarla afecta a los embeds donde se referencia.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Descripción</Label>
                <Textarea
                  value={draft.description ?? ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Texto del botón</Label>
                <Input
                  value={draft.submit_label ?? ''}
                  placeholder="Enviar"
                  onChange={(e) => setDraft({ ...draft, submit_label: e.target.value })}
                />
              </div>
              <ToggleRow
                label="Activo"
                description="Si está desactivado, el formulario no acepta envíos."
                value={draft.is_active !== false}
                onChange={(v) => setDraft({ ...draft, is_active: v })}
              />
              <ToggleRow
                label="Guardar envíos en BD"
                description="Si desactivas, solo se envían notificaciones/webhook."
                value={draft.store_submissions !== false}
                onChange={(v) => setDraft({ ...draft, store_submissions: v })}
              />
            </SectionCard>

            <SectionCard title="Comportamiento al enviar" description="Mensaje de éxito o redirección.">
              <div className="space-y-1">
                <Label>Mensaje de éxito</Label>
                <Textarea
                  value={draft.success_message ?? ''}
                  placeholder="Gracias, te contactaremos pronto."
                  onChange={(e) => setDraft({ ...draft, success_message: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Redirección al enviar (opcional)</Label>
                <Input
                  value={draft.redirect_url ?? ''}
                  placeholder="https://…/gracias"
                  onChange={(e) => setDraft({ ...draft, redirect_url: e.target.value })}
                />
              </div>
              <ToggleRow
                label="Honeypot (anti-bot)"
                description="Añade un campo trampa invisible. Recomendado."
                value={draft.honeypot_enabled !== false}
                onChange={(v) => setDraft({ ...draft, honeypot_enabled: v })}
              />
              <ToggleRow
                label="reCAPTCHA v3"
                description="Requiere RECAPTCHA_SITE_KEY/SECRET configuradas."
                value={!!draft.recaptcha_enabled}
                onChange={(v) => setDraft({ ...draft, recaptcha_enabled: v })}
              />
            </SectionCard>

            <SectionCard title="Notificaciones" description="Recibe los envíos por email/webhook.">
              <div className="space-y-1">
                <Label>Destinatarios (uno por línea)</Label>
                <Textarea
                  rows={3}
                  value={(draft.notifications?.recipients ?? []).join('\n')}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      notifications: {
                        ...draft.notifications,
                        recipients: e.target.value
                          .split('\n').map((r) => r.trim()).filter(Boolean),
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Asunto del email</Label>
                <Input
                  value={draft.notifications?.subject ?? ''}
                  placeholder="Nuevo envío de {{form_title}}"
                  onChange={(e) => setDraft({
                    ...draft,
                    notifications: { ...draft.notifications, subject: e.target.value },
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label>Campo de Reply-To</Label>
                <Input
                  value={draft.notifications?.reply_to_field ?? ''}
                  placeholder="email"
                  onChange={(e) => setDraft({
                    ...draft,
                    notifications: { ...draft.notifications, reply_to_field: e.target.value },
                  })}
                />
                <p className="text-[10px] text-neutral-400">Nombre de un campo del formulario tipo email.</p>
              </div>
              <div className="space-y-1">
                <Label>Webhook URL (POST JSON)</Label>
                <Input
                  value={draft.notifications?.webhook_url ?? ''}
                  placeholder="https://hooks.example.com/…"
                  onChange={(e) => setDraft({
                    ...draft,
                    notifications: { ...draft.notifications, webhook_url: e.target.value },
                  })}
                />
              </div>
            </SectionCard>

            <SectionCard title="Integraciones" description="Conecta con tu CRM, Mailchimp o Zapier.">
              <div className="space-y-1">
                <Label>CRM Provider</Label>
                <select
                  value={draft.integrations?.crm?.provider ?? ''}
                  onChange={(e) => setDraft({
                    ...draft,
                    integrations: {
                      ...draft.integrations,
                      crm: { ...draft.integrations?.crm, provider: e.target.value || undefined },
                    },
                  })}
                  className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
                >
                  <option value="">— Ninguno —</option>
                  <option value="hubspot">HubSpot</option>
                  <option value="pipedrive">Pipedrive</option>
                  <option value="salesforce">Salesforce</option>
                  <option value="custom">Custom webhook</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Endpoint / Token</Label>
                <Input
                  value={draft.integrations?.crm?.endpoint ?? ''}
                  placeholder="https://api.crm.com/leads"
                  onChange={(e) => setDraft({
                    ...draft,
                    integrations: {
                      ...draft.integrations,
                      crm: { ...draft.integrations?.crm, endpoint: e.target.value },
                    },
                  })}
                />
                <Input
                  type="password"
                  value={draft.integrations?.crm?.token ?? ''}
                  placeholder="Token / API key"
                  onChange={(e) => setDraft({
                    ...draft,
                    integrations: {
                      ...draft.integrations,
                      crm: { ...draft.integrations?.crm, token: e.target.value },
                    },
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label>Zapier hook URL</Label>
                <Input
                  value={draft.integrations?.zapier?.hook_url ?? ''}
                  placeholder="https://hooks.zapier.com/…"
                  onChange={(e) => setDraft({
                    ...draft,
                    integrations: {
                      ...draft.integrations,
                      zapier: { hook_url: e.target.value },
                    },
                  })}
                />
              </div>
              <AcelleFormIntegration
                value={draft.integrations?.acelle ?? {}}
                onChange={(acelle) => setDraft({
                  ...draft,
                  integrations: { ...draft.integrations, acelle },
                })}
              />

              <div className="rounded-md bg-neutral-50 p-2 text-xs text-neutral-600">
                <LinkIcon className="mr-1 inline size-3" />
                Usa el bloque <code className="font-mono">form</code> y referencia la key{' '}
                <code className="rounded bg-white px-1 font-mono">{draft.key}</code> desde cualquier página.
              </div>
            </SectionCard>
          </div>
        </TabsContent>

        {/* SUBMISSIONS */}
        <TabsContent value="submissions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>Envíos</CardTitle>
                  <CardDescription>{subs?.total ?? 0} respuestas recibidas.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Buscar…"
                      className="pl-9"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={downloadCsv}>
                    <Download className="size-4" /> CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="px-4 py-2">Fecha</th>
                      <th className="px-4 py-2">Preview</th>
                      <th className="px-4 py-2">UTM</th>
                      <th className="px-4 py-2">IP</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subsLoading && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
                    )}
                    {!subsLoading && filteredSubs.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Sin envíos.</td></tr>
                    )}
                    {filteredSubs.map((s) => (
                      <tr
                        key={s.id}
                        onClick={() => setSelectedSub(s)}
                        className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-neutral-500">
                          {new Date(s.created_at).toLocaleString('es')}
                        </td>
                        <td className="px-4 py-3 max-w-md truncate">
                          {previewPayload(s.payload, draft.fields ?? [])}
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-500">
                          {s.utm_source ? <Badge variant="outline">{s.utm_source}</Badge> : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-400">{s.ip ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('¿Eliminar este envío?')) removeSub.mutate(s.id);
                            }}
                            className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {subs && subs.last_page > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
                  <span>Página {subs.current_page} de {subs.last_page}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                    <Button size="sm" variant="outline" disabled={page >= subs.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Submission detail dialog */}
      <Dialog open={!!selectedSub} onOpenChange={(o) => !o && setSelectedSub(null)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Envío #{selectedSub?.id}</DialogTitle>
            <DialogDescription>
              <Calendar className="mr-1 inline size-3" />
              {selectedSub && new Date(selectedSub.created_at).toLocaleString('es')}
            </DialogDescription>
          </DialogHeader>
          {selectedSub && (
            <div className="space-y-3">
              <dl className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
                {Object.entries(selectedSub.payload).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[150px_1fr] gap-3 px-3 py-2 text-sm">
                    <dt className="font-medium text-neutral-500">{labelFor(k, draft.fields ?? [])}</dt>
                    <dd className="break-words text-neutral-900">
                      {typeof v === 'string' ? v : JSON.stringify(v)}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500">
                {selectedSub.ip && <div>IP: {selectedSub.ip}</div>}
                {selectedSub.referer && <div className="truncate">Referer: {selectedSub.referer}</div>}
                {selectedSub.utm_source && <div>UTM source: {selectedSub.utm_source}</div>}
                {selectedSub.utm_campaign && <div>UTM campaign: {selectedSub.utm_campaign}</div>}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (confirm('¿Eliminar este envío?')) removeSub.mutate(selectedSub.id);
                  }}
                >
                  <Trash2 className="size-4" /> Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionCard({
  title, description, children,
}: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function ToggleRow({
  label, description, value, onChange,
}: { label: string; description?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3 rounded-md border border-neutral-200 p-3">
      <div>
        <div className="text-sm font-medium text-neutral-900">{label}</div>
        {description && <p className="text-xs text-neutral-500">{description}</p>}
      </div>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 size-4 accent-neutral-900"
      />
    </label>
  );
}

function labelFor(k: string, fields: FormFieldData[]): string {
  return fields.find((f) => f.name === k)?.label ?? k;
}

function previewPayload(payload: Record<string, unknown>, fields: FormFieldData[]): string {
  const order = fields.map((f) => f.name);
  const parts: string[] = [];
  for (const key of order) {
    const v = payload[key];
    if (v != null && v !== '' && typeof v !== 'object') {
      parts.push(`${labelFor(key, fields)}: ${String(v)}`);
      if (parts.join(' · ').length > 120) break;
    }
  }
  return parts.join(' · ') || '—';
}

// Ensure each field has a stable local id for the DnD context.
function ensureIds(fields: FormFieldData[]): FormFieldData[] {
  return fields.map((f) => (f.id ? f : { ...f, id: Math.random().toString(36).slice(2, 10) }));
}

type AcelleListItem = { uid: string; name: string };
type AcelleCfg = { enabled?: boolean; list_uid?: string; tag?: string; double_optin?: boolean };

/** Integración del formulario con una lista de Acelle (email marketing). */
function AcelleFormIntegration({ value, onChange }: {
  value: AcelleCfg; onChange: (v: AcelleCfg) => void;
}) {
  const [lists, setLists] = useState<AcelleListItem[]>([]);
  const load = useMutation({
    mutationFn: () => unwrap<AcelleListItem[]>(api.get('/settings/acelle/lists')),
    onSuccess: (data) => { setLists(data); toast.success(`${data.length} lista(s) cargada(s).`); },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudieron cargar las listas. Configura Acelle en Ajustes primero.';
      toast.error(msg);
    },
  });

  const known = new Set(lists.map((l) => l.uid));
  const options = [
    ...lists,
    ...(value.list_uid && !known.has(value.list_uid)
      ? [{ uid: value.list_uid, name: `${value.list_uid} (no cargada)` }]
      : []),
  ];

  return (
    <div className="space-y-3 rounded-md border border-neutral-200 p-3">
      <label className="flex items-center gap-2 text-sm font-medium text-neutral-800">
        <input
          type="checkbox"
          checked={!!value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        Suscribir a Acelle los envíos de este formulario
      </label>

      {value.enabled ? (
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label>Lista de Acelle</Label>
              <select
                value={value.list_uid ?? ''}
                onChange={(e) => onChange({ ...value, list_uid: e.target.value })}
                className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
              >
                <option value="">— Lista por defecto de Acelle —</option>
                {options.map((l) => <option key={l.uid} value={l.uid}>{l.name}</option>)}
              </select>
            </div>
            <Button type="button" variant="secondary" onClick={() => load.mutate()} disabled={load.isPending}>
              {load.isPending ? 'Cargando…' : 'Cargar listas'}
            </Button>
          </div>
          <div className="space-y-1">
            <Label>Etiqueta (opcional)</Label>
            <Input
              value={value.tag ?? ''}
              placeholder="contacto, web"
              onChange={(e) => onChange({ ...value, tag: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={!!value.double_optin}
              onChange={(e) => onChange({ ...value, double_optin: e.target.checked })}
            />
            Requiere confirmación (doble opt-in)
          </label>
        </div>
      ) : null}
    </div>
  );
}

// Strip local-only fields before sending to API.
function serialize(d: FormDetail): Record<string, unknown> {
  return {
    title: d.title,
    description: d.description ?? null,
    submit_label: d.submit_label ?? null,
    fields: (d.fields ?? []).map(({ id: _id, ...rest }) => rest),
    notifications: d.notifications && Object.keys(d.notifications).length ? d.notifications : null,
    integrations: d.integrations && Object.keys(d.integrations).length ? d.integrations : null,
    success_message: d.success_message ?? null,
    redirect_url: d.redirect_url || null,
    honeypot_enabled: d.honeypot_enabled !== false,
    recaptcha_enabled: !!d.recaptcha_enabled,
    store_submissions: d.store_submissions !== false,
    is_active: d.is_active !== false,
  };
}
